<script setup lang="ts">
import {
  IonIcon,
  IonPage,
  IonContent,
  IonItem,
  IonLabel,
  IonLoading,
  IonList,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardSubtitle,
  IonRefresher,
  IonButton,
  IonRefresherContent,
  isPlatform,
  getPlatforms,
} from "@ionic/vue";
import { checkmarkSharp, copyOutline } from "ionicons/icons";
import { computed, reactive, onMounted, watch, toRaw } from "vue";
import { useAppStore } from "@/stores/app";
import { useUserStore } from "@/stores/user";
import { useToasters } from "@/composition/toasters";
import { useWindowWidth } from "@/composition/window";
import {
  Purchases,
  PurchasesPackage,
  LOG_LEVEL,
  PURCHASES_ERROR_CODE,
} from "@revenuecat/purchases-capacitor";
import { Clipboard } from "@capacitor/clipboard";

import { createClientPlan, storeCharge } from "@/api/plans";
// import { SubscriptionCard } from "@/views/Subscription/SubscriptionCard.vue";

const { isMobile } = useWindowWidth();
const toasters = useToasters();
const userStore = useUserStore();
const appStore = useAppStore();

// Set this revenuecat variables as non reactive
// let package_to_subscribe: PurchasesPackage;

export interface IParamsConfirmSubscription {
  store_name: string;
  plan_id: number;
  price: number;
  subscription_id: string;
  current_period_end: number;
  currency: string;
}

const dark_mode = computed(() => appStore.settings.theme === "dark");

const state = reactive({
  loading: false,
  payment_response: {},
  is_pro: false,
  plan_limits: {
    free: {
      Events: 1,
      Visitors: 500,
      "Event Managers": 3,
    },
    default: {},
    Plus: {},
    Starter: {},
  },
  plan_descriptions: {
    free: "The no-cost starter package designed to empower everyone with an intuitive check-in solution",
    default: "",
    Plus: "",
    Starter: "",
  },
  payment_plan_id: {
    default: null,
    Plus: null,
    Starter: null,
  },
  current_plan: {},
  billing_cycle: "MONTHLY",
  managementURL: "",
  status: "",
  plans: [],
  revenue_plans_raw: {},
  successfull_purchase: false,
  customerInfo: {},
  plataform: "",
  debug_steps: [""],
});

onMounted(async () => {
  getContextPlatform();
  await loadBaseInformation().then(async () => {
    await getCustomerInfo();
  });
});

watch(
  () => userStore.me.id,
  async () => {
    if (userStore.me.id) {
      await loadBaseInformation();
    }
  }
);

const loadBaseInformation = async () => {
  state.plans = [];
  try {
    await getSubscriptions().then(() => {
      const plan_keys = Object.keys(state.revenue_plans_raw.all);
      plan_keys.reverse().forEach((key) => {
        const package_data = state.revenue_plans_raw.all[key];
        state.plans = [...state.plans, ...package_data.availablePackages];
        state.plan_limits[key] = {
          Events: package_data.metadata.check_in_max_events,
          Visitors: package_data.metadata.check_in_max_capacity,
          "Event Managers": package_data.metadata.check_in_max_event_managers,
        };
        state.plan_descriptions[key] = package_data.serverDescription;
        state.payment_plan_id[key] = {
          monthly: package_data.metadata.monthly_payment_plan_id,
          annual: package_data.metadata.annual_payment_plan_id,
        };
      });
      getCustomerInfo();
    });
    state.loading = false;
  } catch (e) {
    toasters.addError("Error getting available subscriptions");
    state.loading = false;
  }
};

const getContextPlatform = () => {
  if (isPlatform("ios")) {
    state.plataform = "ios";
  }
  if (isPlatform("android")) {
    state.plataform = "android";
  }
  if (!state.plataform) {
    state.plataform = getPlatforms().join(",");
  }
};

const getCustomerInfo = async () => {
  try {
    await Purchases.getCustomerInfo().then((customerInfo) => {
      state.customerInfo = customerInfo;
      const active_subscriptions = Object.keys(
        customerInfo.customerInfo.entitlements.active
      );
      if (active_subscriptions.length) {
        // Get active subscription
        state.current_plan =
          customerInfo.customerInfo.entitlements.active[
          active_subscriptions[0]
          ];
        if (customerInfo.customerInfo.managementURL) {
          state.managementURL = customerInfo.customerInfo.managementURL;
        }
      }
    });
  } catch (e) {
    state.loading = false;
    throw e;
  }
};

const getSubscriptions = async () => {
  // state.loading = true;
  const googleApiKey = "goog_mwGkgkMqekKJwWbgNyacBCoMApA";
  const appleApiKey = "appl_IkbMXySFGfuZCmCInEumggLumSj";

  await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG }); // Enable to get debug logs

  try {
    if (state.plataform === "android") {
      await Purchases.configure({
        apiKey: googleApiKey,
        appUserID: userStore.related.clients[0].id.toString(),
      });
    }
    if (state.plataform.includes("ios")) {
      console.log("iOS platform!!!");
      await Purchases.configure({
        apiKey: appleApiKey,
        appUserID: userStore.related.clients[0].id.toString(),
      });
    }
  } catch (e) {
    state.loading = false;
    throw e;
  }
  await Purchases.getOfferings().then((offerings) => {
    state.revenue_plans_raw = offerings;
  });
  state.loading = false;
};

const getCurrentPlanName = () => {
  if (Object.keys(state.current_plan).length) {
    return (
      state.current_plan.productIdentifier +
      ":" +
      state.current_plan.productPlanIdentifier
    );
  } else {
    return "";
  }
};
const copy = async (txt: string) => {
  await Clipboard.write({
    string: txt,
  }).then(() => {
    toasters.addSuccess("Copied!");
  });
};

// const callExample = async () => {
//   const params = {
//     store_name: "Google Play",
//     plan_id: 37,
//     price: 12000,
//     subscription_id: "test-monthly-123",
//     current_period_end: 1703808000,
//   };
//   await confirmSubscription(params);
// };

const confirmSubscription = async (params: IParamsConfirmSubscription) => {
  state.debug_steps.push("ConfirmSubscription called");

  const cpp_payload = {
    client_account: userStore.related.clients[0].id,
    payment_plan: params.plan_id,
    billing_cycle: state.billing_cycle.toLowerCase(),
    payment_option: params.store_name,
    currency: params.currency,
  };
  createClientPlan(cpp_payload)
    .then((response) => {
      state.debug_steps.push(
        "createClientPlan called => status " + response.status
      );
      state.debug_steps.push("Preparing to confirm a store charge");

      const store_payload = {
        price: params.subscription_id.includes("starter")
          ? 12000
          : params.price,
        currency: params.currency,
        store: params.store_name.replace(" ", ""),
        subscription_status: "active",
        subscription_id: params.subscription_id,
        current_period_end: params.current_period_end,
      };
      const client_payment_plan = response.data.id;
      storeCharge(client_payment_plan, store_payload)
        .then(async (response_store_charge) => {
          state.debug_steps.push(
            "storeCharge called => status " + response_store_charge.status
          );
          state.debug_steps.push("New plan subscribed successfully!");
          toasters.addSuccess("New plan subscribed successfully!");
          await loadBaseInformation(); // reload data to reflect changes
        })
        .catch((err) => {
          state.debug_steps.push(
            "storeCharge error => " + JSON.stringify(err.response)
          );
          toasters.addError("Error while confirming a new subscription");
        });
    })
    .catch((err) => {
      state.debug_steps.push(
        "Error while creating a Client Payment Plan => " +
        JSON.stringify(err.response)
      );
      toasters.addError("Error while updating customer plan");
    });
};

const WarningOnlyOneActivePlanAllowed = () => {
  toasters.addWarning("You can only have one active subscription at a time.");
};

const subscribePlan = async (
  pack: PurchasesPackage,
  plan_identifier: string,
  plan_price: number
) => {
  state.debug_steps.push("Click to subscribe plan => " + plan_identifier);
  console.log("Click to subscribe plan => " + plan_identifier);

  try {
    if (Object.keys(state.current_plan).length) {
      WarningOnlyOneActivePlanAllowed();
    } else {
      let payment_plan_id: number;

      if (state.billing_cycle === "MONTHLY") {
        payment_plan_id = state.payment_plan_id[plan_identifier].monthly;
      } else {
        payment_plan_id = state.payment_plan_id[plan_identifier].annual;
      }
      if (payment_plan_id) {
        console.log(
          "Before purchase => payment_plan_id " + payment_plan_id.toString()
        );
        await Purchases.purchasePackage({
          aPackage: toRaw(pack),
        }).then(async (purchaseResult) => {
          state.debug_steps.push(
            "Subscribed => " +
            purchaseResult.customerInfo.activeSubscriptions[0]
          );
          console.log(
            "Subscribed => " +
            purchaseResult.customerInfo.activeSubscriptions[0]
          );

          state.debug_steps.push(
            "CustomerInfo active plans => " +
            Object.keys(purchaseResult.customerInfo.entitlements.active).join(
              ", "
            )
          );
          console.log(
            "CustomerInfo active plans => " +
            Object.keys(purchaseResult.customerInfo.entitlements.active).join(
              ", "
            )
          );

          let active_plan_name =
            pack.offeringIdentifier === "default"
              ? "premium"
              : pack.offeringIdentifier;

          console.log(
            "Trying to get active plan => " + active_plan_name.toLowerCase()
          );

          const active_plan =
            purchaseResult.customerInfo.entitlements.active[
            active_plan_name.toLowerCase()
            ];

          state.debug_steps.push(
            "Expiration date (ms) => " + active_plan.expirationDateMillis
          );

          const store_origin = active_plan.store;
          state.debug_steps.push("Store origin => " + store_origin);

          let store_name =
            store_origin === "PLAY_STORE" ? "Google Play" : "Apple App Store";

          state.debug_steps.push("Store name => " + store_name);

          state.debug_steps.push(
            "Preparing to create a new client-payment-plan"
          );

          const params = {
            store_name: store_name,
            plan_id: payment_plan_id,
            price: plan_price * 100,
            subscription_id: purchaseResult.customerInfo.activeSubscriptions[0],
            current_period_end: active_plan.expirationDateMillis / 1000,
            subscription_name:
              purchaseResult.customerInfo.activeSubscriptions[0],
            currency: pack.product.currencyCode.toLowerCase(),
          };

          // Confirm subscription to our backend
          await confirmSubscription(params);
          // Reload data to reflect changes
          await loadBaseInformation();
        });
      }
    }
  } catch (error: any) {
    console.log("PurchasePackage error");
    console.log(error);
    state.loading = false;
    if (error.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
      // Purchase cancelled
    } else {
      // Error making purchase
    }
  }
};

const handleRefresh = async (event: any) => {
  await loadBaseInformation();
  if (event.target) {
    event.target.complete();
  }
};

// const selectBillingCycle = (type: string) => {
//   state.billing_cycle = type;
// };
</script>

<template>
  <ion-page>
    <ion-content v-if="isMobile">
      <h1 class="ion-margin ion-padding-bottom">
        Select Plan
      </h1>
      <!-- <span class="ion-margin"
        >Platform: {{ state.plataform }} | Billing Cycle:
        {{ state.billing_cycle }}</span
      ><br /> -->
      <!-- <span class="ion-margin"
        >Customer Info
        <ion-icon
          :icon="copyOutline"
          color="black"
          size="small"
          @click="copy(JSON.stringify(state.customerInfo))"
        ></ion-icon>
      </span> -->
      <!-- <p class="ion-margin" v-for="step in state.debug_steps" :key="step">
        -> {{ step }}
        <ion-icon
          :icon="copyOutline"
          color="black"
          size="small"
          @click="copy(step)"
        ></ion-icon>
      </p> -->
      <ion-refresher v-if="isMobile" slot="fixed" @ionRefresh="handleRefresh($event)">
        <ion-refresher-content></ion-refresher-content>
      </ion-refresher>
      <!-- <ion-grid v-if="state.plans.length">
        <ion-row class="ion-justify-content-center ion-text-uppercase">
          <ion-col size="3">
            <ion-chip
              :color="
                state.billing_cycle === 'MONTHLY' ? 'primary' : 'transparent'
              "
              @click="selectBillingCycle('MONTHLY')"
              >Monthly</ion-chip
            >
          </ion-col>
          <ion-col size="3">
            <ion-chip
              :color="
                state.billing_cycle === 'ANNUAL' ? 'primary' : 'transparent'
              "
              @click="selectBillingCycle('ANNUAL')"
              >Anually</ion-chip
            >
          </ion-col>
        </ion-row>
      </ion-grid> -->
      <!-- <ion-list lines="none">
        <ion-item class="ion-text-uppercase"> </ion-item>
      </ion-list> -->
      <!-- <ion-button class="ion-margin-top" size="small">
        <a
          href="https://play.google.com/store/account/subscriptions"
          target="_blank"
          class="text-white"
          >MY SUBSCRIPTIONS</a
        >
      </ion-button> -->

      <!-- <SubscriptionCard
        :plan="state.plans[0]"
        :limits="state.plan_limits[state.plans[0].id]"
      /> -->
      <ion-card class="ion-margin" v-if="state.plans.length">
        <ion-card-header>
          <ion-card-title>Free Plan</ion-card-title>
          <ion-card-subtitle :class="dark_mode ? 'dark_mode_price' : ''">€ 0
          </ion-card-subtitle>
        </ion-card-header>

        <div class="ion-margin">
          <ion-button fill="outline" class="select_plan_button"
            v-if="Object.keys(state.current_plan).length === 0">Currently subscribed</ion-button>
          <ion-button v-else class="select_plan_button" @click="WarningOnlyOneActivePlanAllowed()">Select Free
            Plan</ion-button>
        </div>

        <ion-list :inset="true">
          <ion-item :class="dark_mode ? 'dark_mode_item limits' : 'limits'" v-for="(limit, i) in state.plan_limits.free"
            :key="limit">
            <ion-icon aria-hidden="true" :icon="checkmarkSharp" color="success" slot="start"></ion-icon>
            <ion-label>{{ limit }} {{ i }}</ion-label>
          </ion-item>
        </ion-list>
        <h6 class="ion-margin">
          {{ state.plan_descriptions.free }}
        </h6>
      </ion-card>
      <ion-card class="ion-margin" v-for="plan in state.plans.filter(
      (plan) => plan.packageType === state.billing_cycle
    )" :key="plan">
        <ion-card-header>
          <ion-card-title>{{ plan.product.title.replace("(Check-in by beamian)", "") }}
          </ion-card-title>
          <ion-card-subtitle v-if="plan.product.priceString" :class="dark_mode ? 'dark_mode_price' : ''">{{
      plan.product.priceString }}
            <span v-if="plan.packageType === 'ANNUAL'">/ year</span>
            <span v-else>/ month</span>
          </ion-card-subtitle>
        </ion-card-header>

        <div class="ion-margin">
          <ion-button fill="outline" class="select_plan_button"
            v-if="getCurrentPlanName() === plan.product.identifier">Currently subscribed</ion-button>
          <ion-button v-else class="select_plan_button" @click="
      subscribePlan(plan, plan.offeringIdentifier, plan.product.price)
      ">Select
            {{
      plan.product.title.replace("(Event Check-In App)", "")
    }}</ion-button>
        </div>
        <ion-list :inset="true">
          <ion-item :class="dark_mode ? 'dark_mode_item limits' : 'limits'"
            v-for="(limit, i) in state.plan_limits[plan.offeringIdentifier]" :key="limit">
            <ion-icon aria-hidden="true" :icon="checkmarkSharp" color="success" slot="start"></ion-icon>
            <ion-label>{{ limit }} {{ i }}</ion-label>
          </ion-item>
        </ion-list>
        <h6 class="ion-margin">
          {{ state.plan_descriptions[plan.offeringIdentifier] }}
        </h6>
        <div class="ion-margin">
          <ion-button fill="clear" class="select_plan_button" v-if="getCurrentPlanName() === plan.product.identifier">
            <a :href="`${state.managementURL}`" target="_blank" class="text_no_decoration">
              Manage Subscription
            </a>
          </ion-button>
        </div>
      </ion-card>
      <ion-row class="ion-justify-content-center ion-margin" v-if="state.plans.length">
        <a class="text_no_decoration terms_and_privacy"
          href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank"
          v-if="state.plataform === 'ios'">Terms of Service</a>
        <span class="text_no_decoration terms_and_privacy" v-if="state.plataform === 'ios'">&nbsp;and&nbsp;</span>

        <a class="text_no_decoration terms_and_privacy" href="https://beamian.com/terms/" target="_blank">Privacy
          Policy</a>
      </ion-row>
      <ion-loading :is-open="state.loading" message="Please wait..."></ion-loading>
    </ion-content>
  </ion-page>
</template>
<style scoped lang="scss">
.ion-content {
  color: black;
}

ion-card-content {
  font-size: 13px !important;
}

.text-white {
  color: white;
}

ion-card {
  border: 1px solid var(--ion-color-primary);
  cursor: pointer;
}

.limits {
  height: 35px;
}

ion-card-subtitle {
  font-size: 20px;
  color: rgb(66, 66, 66);
}

ion-list {
  background: transparent !important;
  margin-top: 0 !important;
  padding-bottom: 0px;
}

ion-card-header {
  padding-bottom: 0px;
}

ion-button.select_plan_button {
  width: 100%;
  --border-width: 1px;
  margin-bottom: 0px;
}

.text_no_decoration {
  text-decoration: none;
}

.terms_and_privacy {
  font-size: 15px;
}

.dark_mode_price {
  color: #8d8d8d;
}

.dark_mode_item {
  color: #8d8d8d;
  --ion-item-background: #1e1e1e;
}
</style>
